/**
 * Works out whether the page is running inside the PrismSMP Staff Portal desktop app or a normal
 * browser.
 *
 * The app announces itself by adding `PrismStaffApp/<version> (<platform>)` to its User-Agent. It may
 * also expose `window.PrismStaffApp = { version, platform }`; either is enough. The server reads the
 * same User-Agent token (src/lib/client.ts), so both ends agree.
 */

export interface ClientInfo {
  isApp: boolean;
  version: string | null;
  platform: string | null;
}

declare global {
  interface Window {
    PrismStaffApp?: {
      version?: string;
      platform?: string;
      /** Desktop app only: a real Windows notification. Title ≤63, body ≤255 chars; `url` is a path
       *  on this site, opened when the notification is clicked. Rate-limited to one per 2 s. */
      notify?: (title: string, body: string, url?: string) => Promise<boolean>;
    };
  }
}

const UA_TOKEN = /PrismStaffApp\/([\w.+-]+)(?:\s*\(([^)]*)\))?/;

export function detectClient(): ClientInfo {
  const bridge = typeof window !== "undefined" ? window.PrismStaffApp : undefined;
  const match = UA_TOKEN.exec(typeof navigator !== "undefined" ? navigator.userAgent : "");
  if (!bridge && !match) return { isApp: false, version: null, platform: null };
  return {
    isApp: true,
    version: bridge?.version ?? match?.[1] ?? null,
    platform: bridge?.platform ?? match?.[2]?.trim() ?? null,
  };
}

/** -1, 0 or 1. Compares dotted numbers ("1.10.0" > "1.9.2"); anything non-numeric compares as text. */
export function compareVersions(a: string, b: string): number {
  const pa = a.replace(/^v/i, "").split(/[.+-]/);
  const pb = b.replace(/^v/i, "").split(/[.+-]/);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const x = pa[i] ?? "0";
    const y = pb[i] ?? "0";
    const nx = Number(x);
    const ny = Number(y);
    const diff = Number.isNaN(nx) || Number.isNaN(ny) ? x.localeCompare(y) : nx - ny;
    if (diff !== 0) return diff < 0 ? -1 : 1;
  }
  return 0;
}
